import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  CircularProgress,
  Pagination,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Article as ArticleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  // PictureAsPdf,
  // InsertDriveFile,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { newsService } from '@/services/news/newsService';
import { useAuth } from '@/hooks';
import { USER_ROLES } from '@/constants/app';
import { News } from '@/constants/types';
import { CreateNewsModal } from '@/components/news/CreateNewsModal';
import { ConfirmDialog } from '@/components/common';
import toast from 'react-hot-toast';
import moment from 'moment';

// type AttachmentInfo = {
//   url: string;
//   isImage: boolean;
//   isPdf: boolean;
//   fileName?: string;
// };

// const getAttachment = (thumb: News['thumbnail']): AttachmentInfo | null => {
//   if (!thumb) return null;
//   if (typeof thumb === 'string') {
//     // Legacy string — assume image
//     return { url: thumb, isImage: true, isPdf: false };
//   }
//   if (!thumb.url) return null;
//   const ct = (thumb.contentType || '').toLowerCase();
//   const explicitType = (thumb.type || '').toLowerCase();
//   const isImage = explicitType === 'image' || ct.startsWith('image/');
//   const isPdf = ct.includes('pdf') || (thumb.fileName || '').toLowerCase().endsWith('.pdf');
//   return { url: thumb.url, isImage, isPdf, fileName: thumb.fileName };
// };

export const NewsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === USER_ROLES.SUPER_ADMIN || user?.role === USER_ROLES.ADMINISTRATOR;

  const [newsList, setNewsList] = useState<News[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingNews, setEditingNews] = useState<News | null>(null);
  const [deletingNews, setDeletingNews] = useState<News | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const limit = 6;

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    fetchNews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch]);

  const fetchNews = async () => {
    setLoading(true);
    try {
      const response = await newsService.getAllNews(page, limit, debouncedSearch || undefined);
      if (response.success) {
        setNewsList(response.data);
        setTotalPages(response.pagination.pages);
      }
    } catch (error) {
      console.error('Failed to fetch news:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const openCreateModal = () => {
    setEditingNews(null);
    setIsModalOpen(true);
  };

  const openEditModal = (news: News, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingNews(news);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingNews(null);
  };

  const requestDelete = (news: News, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingNews(news);
  };

  const confirmDelete = async () => {
    if (!deletingNews) return;
    try {
      setIsDeleting(true);
      await newsService.deleteNews(deletingNews._id);
      toast.success('News deleted successfully');
      setDeletingNews(null);
      // If last item on page got deleted, step back a page
      if (newsList.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        fetchNews();
      }
    } catch (error) {
      console.error('Failed to delete news:', error);
      toast.error('Failed to delete news');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <ArticleIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            News & Updates
          </Typography>
        </Box>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateModal}
            sx={{
              bgcolor: '#0a66c2',
              '&:hover': { bgcolor: '#004182' },
              textTransform: 'none',
              px: 3,
            }}
          >
            Create News
          </Button>
        )}
      </Box>

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search news..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchInput('')} sx={{ p: 0.2 }}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={10}>
          <CircularProgress />
        </Box>
      ) : newsList.length > 0 ? (
        <>
          <Grid container spacing={3}>
            {newsList.map((news) => {
              // const attachment = getAttachment(news.thumbnail);
              return (
                <Grid size={12} key={news._id}>
                  <Card
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                      },
                    }}
                  >
                    {/* Admin actions */}
                    {isAdmin && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 2,
                          display: 'flex',
                          gap: 0.5,
                        }}
                      >
                        <Tooltip title="Edit news">
                          <IconButton
                            size="small"
                            onClick={(e) => openEditModal(news, e)}
                            sx={{
                              bgcolor: 'rgba(255,255,255,0.95)',
                              boxShadow: 1,
                              '&:hover': { bgcolor: 'white' },
                            }}
                          >
                            <EditIcon fontSize="small" color="primary" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete news">
                          <IconButton
                            size="small"
                            onClick={(e) => requestDelete(news, e)}
                            sx={{
                              bgcolor: 'rgba(255,255,255,0.95)',
                              boxShadow: 1,
                              '&:hover': { bgcolor: 'white' },
                            }}
                          >
                            <DeleteIcon fontSize="small" color="error" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}

                    <CardActionArea
                      onClick={() => navigate(`/news/${news._id}`)}
                      sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'stretch' }}
                    >
                      {/* <Box
                        sx={{
                          width: { xs: '100%', sm: 200 },
                          height: { xs: 150, sm: 'auto' },
                          minHeight: { sm: 150 },
                          bgcolor: 'grey.100',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          overflow: 'hidden',
                          position: 'relative',
                        }}
                      >
                        {attachment?.isImage ? (
                          <Box
                            component="img"
                            src={attachment.url}
                            alt={news.title}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : attachment ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, p: 1, textAlign: 'center' }}>
                            {attachment.isPdf ? (
                              <PictureAsPdf sx={{ fontSize: 40, color: 'error.main' }} />
                            ) : (
                              <InsertDriveFile sx={{ fontSize: 40, color: 'primary.main' }} />
                            )}
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 160 }}>
                              {attachment.fileName || 'Attachment'}
                            </Typography>
                          </Box>
                        ) : (
                          <ArticleIcon sx={{ fontSize: 40, color: 'grey.400' }} />
                        )}
                      </Box> */}
                      <CardContent sx={{ flexGrow: 1, p: 3 }}>
                        <Typography
                          variant="caption"
                          color="primary"
                          sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}
                        >
                          {moment(news.createdAt).format('MMMM DD, YYYY')}
                        </Typography>
                        <Typography
                          variant="h5"
                          component="div"
                          sx={{
                            fontWeight: 700,
                            mb: 1.5,
                            mt: 1,
                            color: 'text.primary',
                            pr: isAdmin ? 8 : 0,
                          }}
                        >
                          {news.title}
                        </Typography>
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          sx={{
                            lineHeight: 1.6,
                          }}
                        >
                          {(() => {
                            const text = news.summary || (news.content ? news.content.replace(/<[^>]*>/g, '') : '');
                            const max = 150;
                            if (text.length <= max) return text;
                            return (
                              <>
                                {text.substring(0, max)}...
                                <Box
                                  component="span"
                                  sx={{
                                    color: 'primary.main',
                                    ml: 0.5,
                                    fontWeight: 600,
                                    '&:hover': { textDecoration: 'underline' },
                                  }}
                                >
                                  Read More
                                </Box>
                              </>
                            );
                          })()}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={6}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      ) : (
        <Box
          textAlign="center"
          py={10}
          bgcolor="background.paper"
          borderRadius={2}
          border="1px dashed"
          borderColor="divider"
        >
          <Typography variant="body1" color="text.secondary">
            {isAdmin ? 'No news found. Be the first to create one!' : 'No news found.'}
          </Typography>
        </Box>
      )}

      <CreateNewsModal
        open={isModalOpen}
        onClose={closeModal}
        onSuccess={fetchNews}
        editNews={editingNews}
      />

      <ConfirmDialog
        open={!!deletingNews}
        title="Delete News"
        message={`Are you sure you want to delete "${deletingNews?.title}"? This action cannot be undone.`}
        onConfirm={confirmDelete}
        onCancel={() => !isDeleting && setDeletingNews(null)}
        severity="error"
        confirmText={isDeleting ? 'Deleting...' : 'Delete'}
      />
    </Box>
  );
};
